import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, ArrowRight, PlusCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const SA_FILTERS = [
  { key: "search", type: "search", label: "Search institution name or email", paths: ["name", "contact_email"] },
  { key: "institution_type", type: "select", label: "Accreditation type", path: "institution_type",
    options: [{ value: "institution", label: "Institutional" }, { value: "program", label: "Programmatic" }] },
  { key: "status", type: "select", label: "Status", path: "status",
    options: [
      { value: "submitted", label: "Submitted" }, { value: "under_review", label: "Under review" },
      { value: "site_visit_scheduled", label: "Site visit scheduled" },
      { value: "site_visit_complete", label: "Site visit complete" },
      { value: "decision_pending", label: "Decision pending" },
      { value: "approved", label: "Approved" }, { value: "denied", label: "Denied" },
    ] },
  { key: "payment_status", type: "select", label: "Payment", path: "payment.status",
    options: [
      { value: "unpaid", label: "Unpaid" }, { value: "pending_verification", label: "Pending verification" },
      { value: "verified", label: "Verified" }, { value: "refunded", label: "Refunded" },
    ] },
  { key: "date_from", type: "dateFrom", label: "Submitted from", path: "created_at" },
  { key: "date_to", type: "dateTo", label: "Submitted to", path: "created_at" },
  { key: "is_archived", type: "boolean", label: "Archived", path: "is_archived" },
  { key: "is_sample", type: "boolean", label: "Sample", path: "is_sample" },
];

export default function SchoolApplications() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/schools/applications").then((r) => setApps(r.data)).finally(() => setLoading(false));
  }, []);

  const filters = useFilters(apps, SA_FILTERS, { defaultSort: { field: "created_at", dir: "desc" } });
  const selection = useSelection(filters.filtered);
  const isAdmin = user.role === "admin";
  const isStaff = isAdmin || user.role === "reviewer" || user.role === "accreditation_reviewer" || user.role === "credentialing_reviewer" || user.role === "read_only_auditor";

  return (
    <div className="animate-fade-up" data-testid="school-applications-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">ACIS · School Accreditation</div>
          <h1 className="font-heading text-3xl font-bold text-primary">{isStaff ? "Accreditation queue" : "My school applications"}</h1>
          <p className="text-sm text-slate-500 mt-1">Institutional and program accreditation applications.</p>
        </div>
        {!isStaff && (
          <Link to="/dashboard/schools/new">
            <Button className="bg-primary text-white hover:bg-[#113A64]" data-testid="new-school-app-btn">
              <PlusCircle className="h-4 w-4 mr-2" /> Apply for accreditation
            </Button>
          </Link>
        )}
      </div>

      <FilterPanel
        schemas={SA_FILTERS} values={filters.values} set={filters.set} clear={filters.clear}
        count={filters.count} total={apps.length} activeCount={filters.activeCount}
        sortFields={[
          { key: "created_at", label: "Submitted date" },
          { key: "name", label: "Institution name" },
          { key: "status", label: "Status" },
        ]}
        sort={filters.sort} setSort={filters.setSort} testid="school-apps-filters"
      />

      {isAdmin && <BulkActionsBar collection="school_applications" selection={selection} onDone={() => api.get("/schools/applications").then((r) => setApps(r.data))} />}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                {isAdmin && (
                  <th className="p-4 w-10">
                    <Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="school-apps-select-all" />
                  </th>
                )}
                <th className="p-4">Institution</th>
                <th className="p-4">Type</th>
                <th className="p-4">Location</th>
                <th className="p-4">Status</th>
                <th className="p-4">Submitted</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={isAdmin ? 7 : 6} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && filters.filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="p-12 text-center">
                  <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <div className="font-heading font-semibold text-primary">No school applications</div>
                  <div className="text-sm text-slate-500 mt-1">{isStaff ? "No applications yet." : "Start a new institutional or program accreditation application."}</div>
                </td></tr>
              )}
              {filters.filtered.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50" data-testid={`school-app-row-${a.id}`}>
                  {isAdmin && (
                    <td className="p-4">
                      <Checkbox checked={selection.isSelected(a.id)} onCheckedChange={() => selection.toggle(a.id)} data-testid={`school-app-select-${a.id}`} />
                    </td>
                  )}
                  <td className="p-4">
                    <div className="font-medium text-primary flex items-center gap-2 flex-wrap">
                      {a.name}
                      {a.is_deleted && <span className="text-[9px] uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">Deleted</span>}
                    </div>
                    <div className="text-xs text-slate-500">{a.contact_email}</div>
                  </td>
                  <td className="p-4 text-xs text-slate-700 capitalize">{a.institution_type}</td>
                  <td className="p-4 text-xs text-slate-700">{[a.city, a.state, a.country].filter(Boolean).join(", ")}</td>
                  <td className="p-4"><StatusBadge status={a.status} /></td>
                  <td className="p-4 text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-right">
                    <Link to={`/dashboard/schools/${a.id}`}>
                      <Button variant="ghost" size="sm" className="text-primary">Open <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    submitted: "bg-blue-50 text-blue-800 border-blue-200",
    under_review: "bg-amber-50 text-amber-800 border-amber-200",
    site_visit_scheduled: "bg-violet-50 text-violet-800 border-violet-200",
    site_visit_complete: "bg-indigo-50 text-indigo-800 border-indigo-200",
    decision_pending: "bg-orange-50 text-orange-800 border-orange-200",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    denied: "bg-red-50 text-red-800 border-red-200",
  };
  return <Badge variant="outline" className={`capitalize ${map[status] || map.draft}`}>{(status || "").replace(/_/g, " ")}</Badge>;
}
